import dayjs from 'dayjs';

import { GaugeData } from '../GlobalViewContainer/types';
import {
  AGGREGATE_LINE_KEY,
  appendAggregateToChartData,
  KW_TO_MW_DIVISOR
} from '@/components/PowerChart/utils';
import { subtractDate } from '@/solaris-lens-common-lib/src/utils/dates';
import { transformSocketDataToGauge } from '@/solaris-lens-common-lib/src/utils/turbines';

export type IntervalType =
  | 'live'
  | 'last-hour'
  | 'last-4-hours'
  | 'last-12-hours'
  | 'last-24-hours'
  | 'last-7-days'
  | 'last-14-days'
  | 'last-30-days';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export const transformHistoryData = (
  historyData: any,
  includeAggregate = true
) => {
  if (!historyData?.series || !Array.isArray(historyData.series)) {
    return [];
  }

  const timestamps = historyData.series[0]?.timestamps || [];

  const allMetrics: any[] = [];
  historyData.series.forEach((serie: any) => {
    if (serie.metrics && Array.isArray(serie.metrics)) {
      allMetrics.push(...serie.metrics);
    }
  });

  const dataPoints = timestamps.map((timestamp: string, index: number) => {
    const dataPoint: { timestamp: string; [key: string]: string | number } = {
      timestamp
    };

    allMetrics.forEach((metric: any) => {
      const value = parseFloat(metric.values[index]);
      dataPoint[`${metric.name}`] = isNaN(value) ? 0 : value;
    });

    return dataPoint;
  });

  const reversed = dataPoints.reverse();
  return includeAggregate ? appendAggregateToChartData(reversed) : reversed;
};

export const getDateRangeByInterval = (interval: IntervalType): DateRange => {
  const endDate = dayjs();
  let startDate;

  switch (interval) {
    case 'live':
    case 'last-hour':
      startDate = subtractDate(1, 'hour');
      break;
    case 'last-4-hours':
      startDate = subtractDate(4, 'hour');
      break;
    case 'last-12-hours':
      startDate = subtractDate(12, 'hour');
      break;
    case 'last-24-hours':
      startDate = subtractDate(24, 'hour');
      break;
    case 'last-7-days':
      startDate = subtractDate(7, 'day');
      break;
    case 'last-14-days':
      startDate = subtractDate(14, 'day');
      break;
    case 'last-30-days':
      startDate = subtractDate(30, 'day');
      break;
    default:
      startDate = subtractDate(1, 'hour');
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString()
  };
};

export const getApiInterval = (interval: IntervalType): string => {
  const intervalMap: Record<IntervalType, string> = {
    live: '1m',
    'last-hour': '1m',
    'last-4-hours': '1m',
    'last-12-hours': '5m',
    'last-24-hours': '1h',
    'last-7-days': '1h',
    'last-14-days': '1h',
    'last-30-days': '4h'
  };
  return intervalMap[interval] || '1m';
};

export const intervalOptions = [
  { value: 'live', label: 'Live' },
  { value: 'last-hour', label: 'Last Hour' },
  { value: 'last-4-hours', label: 'Last 4 hours' },
  { value: 'last-12-hours', label: 'Last 12 Hours' },
  { value: 'last-24-hours', label: 'Last 24 Hours' },
  { value: 'last-7-days', label: 'Last 7 Days' },
  { value: 'last-14-days', label: 'Last 14 Days' },
  { value: 'last-30-days', label: 'Last 30 Days' }
];

export const getTurbines = (turbineData: any) => {
  return turbineData.map((turbine: any) => turbine.id);
};

export const formatGenTotalValue = (value: number): string => {
  return Number(value.toFixed(2)).toString();
};

export const getIsoTimestamp = () => {
  const date = new Date();

  date.setMilliseconds(0);

  return date.toISOString();
};

export function getLastGraphValue(arr: any[], key: string) {
  const row = [...arr].reverse().find(item => key in item);

  return row ? row[key] : undefined;
}

export function setDataForGaugesBySocket(
  installedProducts: any[],
  socketData: any
) {
  const socketDataId = socketData?.power?.[0]?.id;
  const turbineData = installedProducts?.find(item => item.id === socketDataId);

  if (!turbineData) return;
  const newGaugeData = transformSocketDataToGauge(
    turbineData as unknown as GaugeData,
    socketData?.power?.[0]
  );
  const replacedGaugeData = installedProducts.map(item =>
    item.id === newGaugeData.id ? newGaugeData : item
  );
  return replacedGaugeData;
}

export function getNewGenTotalHistoryDataBySocket(
  prevHistoryData: any[],
  genTotalMW: number,
  sentAt?: string
) {
  const socketTimestamp = sentAt ?? getIsoTimestamp();
  const lastRow = prevHistoryData[prevHistoryData.length - 1];

  const addToHistoryRow = {
    ...lastRow,
    timestamp: socketTimestamp,
    [AGGREGATE_LINE_KEY]: genTotalMW * KW_TO_MW_DIVISOR
  };

  if (prevHistoryData.length > 99) {
    prevHistoryData.shift();
  }

  return [...prevHistoryData, addToHistoryRow];
}

export function getNewHistoryDataBySocket(
  prevHistoryData: any[],
  socketData: any
) {
  const socketId = socketData?.power?.[0]?.id;
  const socketGenTotalKwAnalog =
    socketData?.power?.[0]?.measures?.genTotalKwAnalog;
  const socketTimestamp = getIsoTimestamp();

  const lastRow = prevHistoryData[prevHistoryData.length - 1];

  const updatedRow = {
    ...lastRow,
    timestamp: socketTimestamp,
    [`${socketId}`]: Number(socketGenTotalKwAnalog)
  };

  const addToHistoryRow = appendAggregateToChartData([updatedRow])[0];

  if (prevHistoryData.length > 99) {
    prevHistoryData.shift();
  }
  const newHistoryData = [...prevHistoryData, addToHistoryRow];

  return newHistoryData;
}
